import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@veritrellis/ui";
import { evaluatePolicy, findMatchedRuleIndex } from "@veritrellis/shared";
import { useMemo, useState } from "react";
import { serializeToPolicy } from "./policySerializer.js";
import {
  defaultDecisionLabel,
  formatConditionChip,
  ruleDecisionLabel,
  ruleTitleFromConditions
} from "../../lib/policyPresentation.js";
import type { BuilderState } from "./builderTypes.js";

type PolicyPreviewPanelProps = {
  state: BuilderState;
  actionType: string;
  environment: "sandbox" | "production";
};

const decisionOutcomeLabel: Record<string, string> = {
  allow: "allowed",
  deny: "denied",
  require_approval: "pending approval",
  modify: "modify required"
};

export const PolicyPreviewPanel = ({ state, actionType, environment }: PolicyPreviewPanelProps) => {
  const [amount, setAmount] = useState("150");
  const [riskLevel, setRiskLevel] = useState<"low" | "medium" | "high" | "">("medium");

  const preview = useMemo(() => {
    const policy = serializeToPolicy(state);
    const amountNum = Number(amount);
    const ctx = {
      environment,
      amount: Number.isNaN(amountNum) ? null : amountNum,
      riskLevel: riskLevel.length > 0 ? riskLevel : null,
      customerTier: null,
      actionSubtype: null,
      riskClass: riskLevel === "low" ? "low" : riskLevel === "high" ? "high" : "medium",
      originSystem: null,
      chainAmountSum: null
    };
    const result = evaluatePolicy(policy, ctx);
    const matchedRuleIndex = findMatchedRuleIndex(policy, ctx);

    return { result, matchedRuleIndex };
  }, [state, amount, riskLevel, environment]);

  return (
    <section className="admin-panel admin-policy-preview" aria-labelledby="policy-preview-heading">
      <div className="admin-panel-header">
        <h2 id="policy-preview-heading" className="admin-panel-title">
          Live preview
        </h2>
      </div>
      <div className="admin-panel-body-padded">
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--muted-foreground)" }}>
          Sample request for <code style={{ fontSize: 11 }}>{actionType}</code>
        </p>
        <div className="admin-policy-preview-inputs">
          <div>
            <Label>Amount</Label>
            <Input
              type="number"
              className="h-8"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
            />
          </div>
          <div>
            <Label>Risk level</Label>
            <Select
              value={riskLevel.length > 0 ? riskLevel : "__none__"}
              onValueChange={(v) => setRiskLevel(v === "__none__" ? "" : (v as typeof riskLevel))}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Any</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="admin-policy-preview-ladder" style={{ marginTop: 16 }}>
          {state.mode === "conditional" ? (
            <>
              {state.rules.map((rule, i) => (
                <div
                  key={rule.id}
                  className={`admin-policy-preview-step${
                    preview.matchedRuleIndex === i ? " admin-policy-preview-step-matched" : ""
                  }`}
                >
                  <span className="admin-policy-preview-step-index">{i + 1}</span>
                  <div className="admin-policy-preview-step-body">
                    <p className="admin-policy-preview-step-title">
                      {ruleTitleFromConditions(rule)}
                    </p>
                    <p className="admin-policy-preview-step-cond">
                      {rule.conditions.length === 0
                        ? "No conditions"
                        : rule.conditions.map(formatConditionChip).join(" · ")}
                    </p>
                  </div>
                  <span className="admin-policy-preview-step-status">
                    {preview.matchedRuleIndex === i ? "matched" : "—"}
                  </span>
                </div>
              ))}
              <div className="admin-policy-preview-step">
                <span className="admin-policy-preview-step-index">∅</span>
                <div className="admin-policy-preview-step-body">
                  <p className="admin-policy-preview-step-title">Default</p>
                  <p className="admin-policy-preview-step-cond">
                    {defaultDecisionLabel(state.defaultDecision)}
                  </p>
                </div>
                <span className="admin-policy-preview-step-status">
                  {preview.matchedRuleIndex === null && state.mode === "conditional"
                    ? "matched"
                    : "—"}
                </span>
              </div>
            </>
          ) : (
            <div className="admin-policy-preview-step admin-policy-preview-step-matched">
              <span className="admin-policy-preview-step-index">1</span>
              <div className="admin-policy-preview-step-body">
                <p className="admin-policy-preview-step-title">{state.mode.replaceAll("_", " ")}</p>
              </div>
              <span className="admin-policy-preview-step-status">matched</span>
            </div>
          )}
        </div>

        <div className="admin-policy-preview-outcome">
          <span className="admin-policy-preview-outcome-label">Decision</span>
          <Badge
            variant={
              preview.result.decision === "allow"
                ? "success"
                : preview.result.decision === "deny"
                  ? "destructive"
                  : "warning"
            }
          >
            {decisionOutcomeLabel[preview.result.decision] ?? preview.result.decision}
          </Badge>
          {preview.matchedRuleIndex !== null && state.mode === "conditional" ? (
            <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              via rule {preview.matchedRuleIndex + 1} ·{" "}
              {ruleDecisionLabel[state.rules[preview.matchedRuleIndex]!.decision]}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
};
